import * as React from 'react'

import { inject, observer } from 'mobx-react'

import {
  BlockTypeEnum,
  IWorkflowDurationOptions,
  IWorkflowStep,
  WorkflowStepType,
} from '~/client/graph/types.generated'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/client/src/shared/components/shadcn-ui/select'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import WorkflowIconGroups from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import {
  getBlockTypeDisplayName,
  getWorkflowStepDisplayName,
} from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import {
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  MINUTES_IN_WEEK,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import { enumToList } from '~/client/src/shared/utils/converters'
import {
  deadlineOptions,
  getDeadlineOptionText,
  NO_DEADLINE_OPTION,
} from '~/client/src/shared/utils/DeadlineOptions'
import { EMPTY_STRING, NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import { FormGeneralFields } from '../../FormTypeConfigurator.store'
import FormGeneralOptionsDropdown, {
  IFormGeneralTabDropdownOption,
} from './components/FormGeneralOptionsDropdown'
import FormIconDropdown, {
  IFormIconDropdownOption,
} from './components/FormIconDropdown'
import WorkflowDurationConfiguration from './components/WorkflowDurationConfiguration'

// localization: translated

interface IProps {
  formType: WorkflowType

  getMonthDayYearAndTimeToDisplay(date: Date | number): string
  onChange(generalProps: FormGeneralFields): void

  workflowCategoriesStore?: WorkflowCategoriesStore
}

enum TitleMandatority {
  MANDATORY = 'true',
  OPTIONAL = 'false',
}

const blockSortPriority: { [K in BlockTypeEnum]: number } = {
  [BlockTypeEnum.Block]: 1,
  [BlockTypeEnum.Warn]: 2,
  [BlockTypeEnum.Hide]: 3,
}
const allBlockTypes = enumToList(BlockTypeEnum).sort(
  (a, b) => blockSortPriority[a] - blockSortPriority[b],
)

const getTitleOptionName = (isWorkflowTitleMandatory: boolean) =>
  isWorkflowTitleMandatory
    ? Localization.translator.mandatory
    : Localization.translator.optional

@inject('workflowCategoriesStore')
@observer
export default class GeneralFormConfigurator extends React.Component<IProps> {
  public render() {
    const {
      formType,
      getMonthDayYearAndTimeToDisplay,
      workflowCategoriesStore,
    } = this.props
    const {
      name: formTypeName,
      iconName,
      isEnabled,
      categoryId,
      shouldBlockOnNonWorkTimes,
      isWorkflowTitleMandatory,
      bookingDeadlineOptions,
      getFormattedUpdatedAt,
      isAutoEndingEnabled,
      canActivateAutoEnd,
      autoEndStepId,
      recurringOptions,
      maxDurationOptions,
      defaultDurationOptions,
    } = formType

    const lastUpdatedAtLabel = getFormattedUpdatedAt(
      getMonthDayYearAndTimeToDisplay,
    )
    const hasBookingOptions = !!bookingDeadlineOptions?.blockType
    const category = workflowCategoriesStore.getById(categoryId)

    return (
      <div className="col mw400 pl10">
        <StruxhubInput
          className="overflow-hidden no-flex pb10"
          label={Localization.translator.formName}
          isRequired
          isValid
          isHelperTextCondensed
          isDisabled={formType.isActivity}
          value={formTypeName || EMPTY_STRING}
          onChange={this.changeFormName}
        />
        <div className="pb10">
          <FormGeneralOptionsDropdown
            targetClassName="full-width"
            customSelectorLabel={Localization.translator.workflowCategory}
            isRequiredTextShown
            shouldHighlightInvalid
            selectedValue={category?.id}
            options={this.categoriesOptions}
            onItemClick={this.changeCategory}
          />
        </div>
        {!formType.isActivity && (
          <div className="pb10">
            <FormIconDropdown
              targetClassName="full-width"
              selectedValue={iconName}
              options={this.iconOptions}
              onItemClick={this.changeIconName}
            />
          </div>
        )}
        <div className="tw:flex tw:items-center tw:gap-2 tw:py-2.5">
          <div className="text large line-extra-large tw:flex-1">
            {Localization.translator.allowCreation}
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={this.changeAllowCreation}
            aria-label={Localization.translator.allowCreation}
          />
        </div>
        <div className="row py10">
          <div className="text large line-extra-large full-flex-basis">
            {Localization.translator.workflowTitle}
          </div>
          {this.renderWorkflowTitleMenu(isWorkflowTitleMandatory)}
        </div>
        <div className="tw:flex tw:items-center tw:gap-2 tw:py-2.5">
          <div className="text large line-extra-large tw:flex-1">
            {Localization.translator.autoFinishWorkflowOnFinishDate}
          </div>
          <Switch
            checked={isAutoEndingEnabled}
            disabled={!canActivateAutoEnd}
            onCheckedChange={this.toggleAutomaticEnd}
          />
        </div>
        {isAutoEndingEnabled && (
          <div className="pb10">
            <FormGeneralOptionsDropdown
              targetClassName="full-width"
              customSelectorLabel={
                Localization.translator.executeAutomaticFinish
              }
              selectedValue={autoEndStepId}
              options={this.autoEndOptions}
              onItemClick={this.changeAutomaticEndStep}
            />
          </div>
        )}
        <div className="tw:flex tw:items-center tw:gap-2 tw:py-2.5">
          <div className="text large line-extra-large tw:flex-1">
            {Localization.translator.blockRequestsForNonWorkTimes}
          </div>
          <Switch
            checked={shouldBlockOnNonWorkTimes}
            onCheckedChange={this.changeBlockNonWorkTime}
          />
        </div>
        <div className="row py10">
          <div className="text large line-extra-large full-flex-basis">
            {Localization.translator.workflowBookingDeadline}
          </div>
          {this.renderWorkflowBookingDeadlineSelect(
            bookingDeadlineOptions?.blockType || BlockTypeEnum.Hide,
          )}
        </div>
        {hasBookingOptions &&
          bookingDeadlineOptions.blockType !== BlockTypeEnum.Hide && (
            <>
              <span className="text large light pb10">
                {Localization.translator.workflowDeadlineDescr}
              </span>
              <div className="pb10">
                <FormGeneralOptionsDropdown
                  targetClassName="full-width"
                  customSelectorLabel={
                    Localization.translator.workflowsMustBeRequested
                  }
                  selectedValue={String(
                    bookingDeadlineOptions.deadlineInterval || -1,
                  )}
                  options={this.bookingDeadlineOptions}
                  onItemClick={this.changeDeadlineOptions}
                />
              </div>
            </>
          )}

        <WorkflowDurationConfiguration
          onChange={this.changeMaxDuration}
          durationOptions={maxDurationOptions}
          switchLabel={Localization.translator.enableMaximumDuration}
          durationLabel={Localization.translator.maximumDuration}
          durationTimeLabel={Localization.translator.maximumDurationTime}
        />
        <WorkflowDurationConfiguration
          onChange={this.changeDefaultDuration}
          durationOptions={defaultDurationOptions}
          switchLabel={Localization.translator.enableDefaultDuration}
          durationLabel={Localization.translator.defaultDuration}
          durationTimeLabel={Localization.translator.defaultDurationTime}
          maximumDuration={formType.maxDurationInMinutes}
        />
        <div className="tw:flex tw:items-center tw:gap-2 tw:py-2.5">
          <div className="text large line-extra-large tw:flex-1">
            {Localization.translator.allowRepeatingWorkflow}
          </div>
          <Switch
            checked={recurringOptions?.isEnabled}
            onCheckedChange={this.changeRecurringWorkflowEnabled}
          />
        </div>
        {recurringOptions?.isEnabled && (
          <div className="py10">
            <FormGeneralOptionsDropdown
              targetClassName="full-width"
              customSelectorLabel={
                Localization.translator.maximumRepeatingDuration
              }
              isRequiredTextShown
              selectedValue={recurringOptions?.maxDuration?.toString() || ''}
              options={this.recurringDurationOptions}
              onItemClick={this.changeMaxRecurringDuration}
            />
          </div>
        )}
        <div className="row py10 text large line-extra-large">
          <span>{Localization.translator.lastUpdated}</span>
          <span className="no-grow nowrap">{lastUpdatedAtLabel}</span>
        </div>
      </div>
    )
  }

  private renderWorkflowTitleMenu(
    isWorkflowTitleMandatory: boolean,
  ): JSX.Element {
    const selectedValue = isWorkflowTitleMandatory.toString()

    return (
      <Select
        value={selectedValue}
        onValueChange={value =>
          this.changeTitleIsMandatory(value as TitleMandatority)
        }
        items={this.titleOptions.map(option => ({
          value: option.id,
          label: option.name,
        }))}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end" className="tw:w-40">
          {this.titleOptions.map(option => (
            <SelectItem key={option.id} value={option.id}>
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  private renderWorkflowBookingDeadlineSelect(
    selectedValue: BlockTypeEnum,
  ): JSX.Element {
    const items = this.deadlineBlockOptions.map(option => ({
      value: option.id,
      label: option.name,
    }))

    return (
      <Select
        value={selectedValue}
        onValueChange={value =>
          this.changeBookingBlockType(value as BlockTypeEnum)
        }
        items={items}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end" className="tw:w-40">
          {this.deadlineBlockOptions.map(option => (
            <SelectItem key={option.id} value={option.id}>
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  private changeFormName = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange({ name: event.target.value })
  }

  private changeCategory = (categoryId: string) => {
    this.props.onChange({ categoryId })
  }

  private changeAllowCreation = () => {
    this.props.onChange({ isEnabled: !this.props.formType.isEnabled })
  }

  private changeIconName = (iconName: string) => {
    this.props.onChange({ iconName })
  }

  private changeBlockNonWorkTime = () => {
    this.props.onChange({
      shouldBlockOnNonWorkTimes: !this.props.formType.shouldBlockOnNonWorkTimes,
    })
  }

  private changeTitleIsMandatory = (
    isWorkflowTitleMandatory: TitleMandatority,
  ) => {
    this.props.onChange({
      isWorkflowTitleMandatory: JSON.parse(isWorkflowTitleMandatory),
    })
  }

  private changeRecurringWorkflowEnabled = () => {
    this.props.onChange({
      recurringOptions: {
        isEnabled: !this.props.formType.recurringOptions?.isEnabled,
        maxDuration: this.props.formType.recurringOptions?.maxDuration,
      },
    })
  }

  private changeMaxRecurringDuration = (maxRecurringDuration: string) => {
    this.props.onChange({
      recurringOptions: {
        isEnabled: this.props.formType.recurringOptions?.isEnabled,
        maxDuration: Number(maxRecurringDuration),
      },
    })
  }

  private toggleAutomaticEnd = () => {
    const { formType, onChange } = this.props

    onChange({
      autoEndStepIds: !formType.autoEndStepId ? [formType.initialStep.id] : [],
    })
  }

  private changeAutomaticEndStep = (stepId: string) => {
    this.props.onChange({
      autoEndStepIds: !stepId ? [] : [stepId],
    })
  }

  private changeBookingBlockType = (blockType: BlockTypeEnum) => {
    const { formType, onChange } = this.props

    let deadlineInterval =
      formType.bookingDeadlineOptions?.deadlineInterval || NO_DEADLINE_OPTION
    if (
      blockType !== BlockTypeEnum.Hide &&
      deadlineInterval === NO_DEADLINE_OPTION
    ) {
      deadlineInterval = 1
    }

    onChange({
      bookingDeadlineOptions: {
        deadlineInterval,
        blockType,
      },
    })
  }

  private changeDeadlineOptions = (deadlineOption: string) => {
    const { formType, onChange } = this.props
    const deadlineInterval = Number(deadlineOption)

    if (deadlineInterval === NO_DEADLINE_OPTION) {
      this.changeBookingBlockType(BlockTypeEnum.Hide)
      return
    }

    onChange({
      bookingDeadlineOptions: {
        deadlineInterval,
        blockType: formType.bookingDeadlineOptions.blockType,
      },
    })
  }

  private get titleOptions(): IFormGeneralTabDropdownOption[] {
    return [
      { id: TitleMandatority.MANDATORY, name: getTitleOptionName(true) },
      { id: TitleMandatority.OPTIONAL, name: getTitleOptionName(false) },
    ]
  }

  private get bookingDeadlineOptions(): IFormGeneralTabDropdownOption[] {
    return deadlineOptions.map(opt => ({
      id: opt.toString(),
      name: getDeadlineOptionText(opt),
    }))
  }

  private get deadlineBlockOptions(): IFormGeneralTabDropdownOption[] {
    return allBlockTypes.map(blockType => ({
      id: blockType,
      name: getBlockTypeDisplayName(blockType),
    }))
  }

  private get autoEndOptions(): IFormGeneralTabDropdownOption[] {
    const { workflowSteps, initialStep } = this.props.formType
    return workflowSteps.reduce((list, step, idx) => {
      if (step.type !== WorkflowStepType.RecurringInspection) {
        list.push({
          id: !idx || idx === 1 ? initialStep.id : step.id,
          name: !idx
            ? Localization.translator.atAnyStep
            : this.getAutoEndStepName(step, idx),
          isDisabled: idx === 1,
        })
      }
      return list
    }, [])
  }

  private get categoriesOptions(): IFormGeneralTabDropdownOption[] {
    return this.props.workflowCategoriesStore.activeCategories.reduce(
      (list, category) => {
        list.push({
          id: category.id,
          name: category.name,
        })
        return list
      },
      [{ id: undefined, name: NO_VALUE }],
    )
  }

  private get iconOptions(): IFormIconDropdownOption[] {
    return WorkflowIconGroups.get().map(group => ({
      desc: group.desc,
      icons: group.icons.map(icon => ({
        name: icon.name,
        desc: icon.desc,
      })),
    }))
  }

  private get recurringDurationOptions() {
    const recurringDurationValues = [
      { days: 7, label: Localization.translator.oneWeek },
      { days: 14, label: Localization.translator.twoWeeks },
      { days: 30, label: Localization.translator.oneMonth },
      { days: 60, label: Localization.translator.twoMonths },
      { days: 90, label: Localization.translator.threeMonths },
      { days: 180, label: Localization.translator.sixMonths },
      { days: 365, label: Localization.translator.twelveMonths },
    ]

    return recurringDurationValues.map(option => ({
      id: String(option.days),
      name: option.label,
    }))
  }

  private getFormDurationText = (value: number): string => {
    const { xHours, xMinutes, xDays, xWeeks } = Localization.translator
    if (value >= MINUTES_IN_WEEK) {
      const weeks = value / MINUTES_IN_WEEK
      return `${weeks} ${xWeeks(weeks)}`
    }
    if (value >= MINUTES_IN_DAY) {
      const days = value / MINUTES_IN_DAY
      return `${days} ${xDays(days)}`
    }
    if (value >= MINUTES_IN_HOUR) {
      return xHours(value / MINUTES_IN_HOUR)
    }
    return xMinutes(value)
  }

  private getAutoEndStepName = (step: IWorkflowStep, index: number): string => {
    const { workflowSteps } = this.props.formType

    const onlyAt =
      index + 1 === workflowSteps.length
        ? Localization.translator.onlyAt
        : Localization.translator.onlyAtOrAfter
    const stepDisplayName = getWorkflowStepDisplayName(
      step.type,
      getWorkflowStepLevel(step.id, workflowSteps),
    )

    return `${onlyAt} ${Localization.translator.step} ${
      index + 1
    }: ${stepDisplayName}`
  }

  private changeMaxDuration = (options: Partial<IWorkflowDurationOptions>) => {
    this.props.onChange({
      maxDurationOptions: {
        ...this.props.formType.maxDurationOptions,
        ...options,
      },
    })
  }

  private changeDefaultDuration = (
    options: Partial<IWorkflowDurationOptions>,
  ) => {
    this.props.onChange({
      defaultDurationOptions: {
        ...this.props.formType.defaultDurationOptions,
        ...options,
      },
    })
  }
}
